import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HardDrive, Plus, Trash2, ArrowLeft, ShieldCheck, Camera, Mic,
  Layers, Video,
} from "lucide-react";
import { toast } from "sonner";

const DEPT_META = {
  camera:            { label: "Camera",      Icon: Camera, color: "#6B4EFF" },
  sound:             { label: "Sound",       Icon: Mic,    color: "#10B981" },
  vfx_plates:        { label: "VFX Plates",  Icon: Layers, color: "#EC4899" },
  monitor_recording: { label: "Monitor Rec", Icon: Video,  color: "#F59E0B" },
};

const STATUS_META = {
  pending:  { label: "Pending",  color: "#F5B82E" },
  received: { label: "Received", color: "#0EA5E9" },
  verified: { label: "Verified", color: "#22C55E" },
  archived: { label: "Archived", color: "#71717A" },
};

export default function ProjectRushes() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [rows, setRows]       = useState([]);
  const [counts, setCounts]   = useState({pending:0, received:0, verified:0, archived:0});
  const [totalGb, setTotalGb] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [meta, setMeta]       = useState({rushes_depts: [], rushes_statuses: []});
  const [openAdd, setOpenAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(blankForm());

  function blankForm() {
    return {
      shoot_date: new Date().toISOString().slice(0, 10),
      department: "camera",
      storage_media: "",
      file_count: "",
      size_gb: "",
      checksum_verified: false,
      handed_over_to: "",
      handoff_date: "",
      status: "pending",
      notes: "",
    };
  }

  async function load() {
    try {
      const [{ data: p }, { data: r }, { data: m }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/rushes`),
        api.get(`/projects/postprod/meta`),
      ]);
      setProject(p.project || p);
      setRows(r.rushes || []);
      setCounts(r.counts || {});
      setTotalGb(r.total_gb || 0);
      setTotalFiles(r.total_files || 0);
      setMeta(m);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load rushes");
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  async function add(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${id}/rushes`, {
        shoot_date:        form.shoot_date,
        department:        form.department,
        storage_media:     form.storage_media,
        file_count:        parseInt(form.file_count) || 0,
        size_gb:           parseFloat(form.size_gb) || 0,
        checksum_verified: form.checksum_verified,
        handed_over_to:    form.handed_over_to,
        handoff_date:      form.handoff_date,
        status:            form.status,
        notes:             form.notes,
      });
      toast.success("Rushes logged");
      setOpenAdd(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    } finally { setSaving(false); }
  }

  async function advanceStatus(row) {
    const order = ["pending", "received", "verified", "archived"];
    const next = order[Math.min(order.indexOf(row.status || "pending") + 1, order.length - 1)];
    try {
      await api.patch(`/projects/${id}/rushes/${row.id}`, { status: next });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  }

  async function toggleChecksum(row) {
    try {
      await api.patch(`/projects/${id}/rushes/${row.id}`, {
        checksum_verified: !row.checksum_verified,
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  }

  async function remove(rowId) {
    if (!window.confirm("Delete this rushes bundle?")) return;
    try {
      await api.delete(`/projects/${id}/rushes/${rowId}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  }

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const k = r.shoot_date || "—";
      (g[k] = g[k] || []).push(r);
    }
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="project-rushes-page">
      <button
        onClick={() => nav(`/p/${id}`)}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-to-project">
        <ArrowLeft size={12} /> Back to Project
      </button>

      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Post-Production · Rushes</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" data-testid="rushes-title">
            {project?.title || "Rushes Tracker"}
          </h1>
          <div className="mt-2 text-sm text-white/55" data-testid="rushes-summary">
            {rows.length} bundle{rows.length===1?"":"s"} · {totalFiles.toLocaleString()} files · {totalGb.toLocaleString()} GB
          </div>
        </div>
        <Button onClick={() => setOpenAdd(true)}
          className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
          data-testid="rushes-add-btn">
          <Plus size={14} className="mr-1.5" /> Log Rushes
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="rushes-status-strip">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <div key={k} className="cs-card rounded-2xl p-4"
            style={{ borderColor: `${v.color}22` }}>
            <div className="text-[9px] uppercase tracking-[0.28em]" style={{ color: v.color }}>{v.label}</div>
            <div className="mt-1 text-2xl font-display tabular-nums" style={{ color: v.color }}
              data-testid={`rushes-count-${k}`}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {rows.length === 0 && (
        <div className="mt-6 cs-card rounded-2xl p-10 text-center" data-testid="rushes-empty">
          <HardDrive size={28} className="mx-auto text-white/40" />
          <div className="mt-3 text-sm text-white/60">No rushes logged yet.</div>
          <div className="mt-1 text-xs text-white/45">Log every shoot-day bundle — SD, SSD or backup path — so editors and DI know exactly what has been handed over.</div>
        </div>
      )}

      {grouped.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="rushes-groups">
          {grouped.map(([date, list]) => (
            <div key={date} className="cs-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white/55 mr-2">Shoot Date</span>
                  {date}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/55">
                  {list.length} bundle{list.length===1?"":"s"}
                </div>
              </div>
              <div className="space-y-2">
                {list.map(r => (
                  <RushRow key={r.id} row={r} onAdvance={advanceStatus}
                    onToggleChecksum={toggleChecksum} onRemove={remove} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-2xl" data-testid="rushes-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Log rushes bundle</DialogTitle>
            <DialogDescription className="text-white/55">One row per drive/SD/SSD handed over — makes checksum + hand-off traceable.</DialogDescription>
          </DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Shoot Date *">
                <Input required type="date" value={form.shoot_date} data-testid="rushes-shoot-date"
                  onChange={e => setForm({...form, shoot_date: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Department">
                <Select value={form.department} onValueChange={(v)=>setForm({...form, department: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="rushes-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.rushes_depts.map(d => (
                      <SelectItem key={d} value={d}>{DEPT_META[d]?.label || d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="rushes-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.rushes_statuses.map(s => (
                      <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-3">
                <Field label="Storage Media">
                  <Input value={form.storage_media} data-testid="rushes-media"
                    onChange={e => setForm({...form, storage_media: e.target.value})}
                    placeholder="SanDisk Extreme SD 128 GB #A · or  s3://backup/day3/…"
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
              <Field label="File Count">
                <Input type="number" value={form.file_count} data-testid="rushes-files"
                  onChange={e => setForm({...form, file_count: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Size (GB)">
                <Input type="number" step="0.1" value={form.size_gb} data-testid="rushes-size"
                  onChange={e => setForm({...form, size_gb: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="flex items-end pb-1.5">
                <label className="flex items-center gap-2 text-xs text-white/70" data-testid="rushes-checksum-toggle">
                  <input type="checkbox" checked={form.checksum_verified}
                    onChange={e => setForm({...form, checksum_verified: e.target.checked})} />
                  <ShieldCheck size={12} /> Checksum verified
                </label>
              </div>
              <Field label="Handed Over To">
                <Input value={form.handed_over_to} data-testid="rushes-handover"
                  onChange={e => setForm({...form, handed_over_to: e.target.value})}
                  placeholder="Editor's name"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Handoff Date">
                <Input type="date" value={form.handoff_date} data-testid="rushes-handoff-date"
                  onChange={e => setForm({...form, handoff_date: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-3">
                <Field label="Notes">
                  <Textarea value={form.notes} data-testid="rushes-notes"
                    onChange={e => setForm({...form, notes: e.target.value})}
                    placeholder="e.g. Reel 3 had a card mount fail — pulled backup from monitor rec"
                    className="bg-black/40 border-white/15 min-h-[60px]" />
                </Field>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={()=>setOpenAdd(false)} data-testid="rushes-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="rushes-submit">
                {saving ? "Saving…" : "Log rushes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RushRow({ row, onAdvance, onToggleChecksum, onRemove }) {
  const dept   = DEPT_META[row.department] || { label: row.department, Icon: HardDrive, color: "#888" };
  const status = STATUS_META[row.status || "pending"];
  const DIcon  = dept.Icon;
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2.5 px-3 rounded-xl border border-white/5 hover:border-white/15"
      data-testid={`rushes-row-${row.id}`}>
      <div className="col-span-1 flex items-center justify-center">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${dept.color}22`, color: dept.color }}>
          <DIcon size={14} />
        </div>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: dept.color }}>{dept.label}</div>
        <div className="text-xs text-white/60 truncate mt-0.5">{row.storage_media || <span className="text-white/30 italic">no media noted</span>}</div>
      </div>
      <div className="col-span-2 text-xs text-white/70 tabular-nums">
        {row.file_count.toLocaleString()} files · {row.size_gb} GB
      </div>
      <div className="col-span-2 text-xs text-white/70 truncate">
        {row.handed_over_to ? `→ ${row.handed_over_to}` : <span className="text-white/30">Not handed over</span>}
      </div>
      <button onClick={() => onToggleChecksum(row)}
        title={row.checksum_verified ? "Checksum verified · click to unset" : "Mark checksum verified"}
        className="col-span-1 flex items-center justify-center"
        data-testid={`rushes-checksum-${row.id}`}>
        <ShieldCheck size={16} className={row.checksum_verified ? "text-emerald-400" : "text-white/25"} />
      </button>
      <div className="col-span-2">
        <button onClick={() => onAdvance(row)}
          className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border w-full text-center"
          style={{ color: status.color, borderColor: `${status.color}55`, background: `${status.color}15` }}
          data-testid={`rushes-advance-${row.id}`}>
          {status.label}
        </button>
      </div>
      <div className="col-span-1 text-right">
        <button onClick={() => onRemove(row.id)} className="p-2 text-white/40 hover:text-red-400"
          data-testid={`rushes-remove-${row.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
